import {
  getNewNodeId,
  isLegacyNode,
  LEGACY_NODES_TO_REMOVE,
  MIGRATION_STATS,
  NODE_ID_MAPPINGS,
} from './nodeIdMappings';
import { PersistedState, STATE_VERSION } from './versioning';

// Control migration logging verbosity
const MIGRATION_DEBUG = process.env.NODE_ENV === 'test' && process.env.MIGRATION_DEBUG === 'true';

// Migration function for Segment 3 parallel worlds restructure
function migrateSegment3Nodes(state: PersistedState): PersistedState {
  if (MIGRATION_DEBUG) console.log('Migrating Segment 3 to parallel worlds structure...');
  if (!state.narrativeState || !state.narrativeState.nodes) {
    return state;
  }

  const nodes = { ...state.narrativeState.nodes };

  // Check if old FinalSequence_Memory_Node exists
  const oldNode = nodes['FinalSequence_Memory_Node'];
  if (!oldNode) {
    return state; // Nothing to migrate
  }

  // Create new good world node
  const {
    textMainGoodWorld: _textMainGoodWorld,
    textMainBadWorld: _textMainBadWorld,
    choicesGoodWorld: _choicesGoodWorld,
    choicesBadWorld: _choicesBadWorld,
    conditionalTextBlocks: _conditionalTextBlocks,
    textBeforeGoodWorld: _textBeforeGoodWorld,
    textAfterGoodWorld: _textAfterGoodWorld,
    textBeforeBadWorld: _textBeforeBadWorld,
    textAfterBadWorld: _textAfterBadWorld,
    ...cleanOldNode
  } = oldNode;

  const goodWorldNode = {
    ...cleanOldNode,
    id: 'S3A_NPC_Recall_Positive',
    segmentId: '3A' as const,
    variantContent: {
      goodWorld: {
        variants: oldNode.variantContent?.variants || [],
        placeholderPosition: oldNode.variantContent?.placeholderPosition || 0,
      },
    },
  };

  // Create new bad world node
  const badWorldNode = {
    ...cleanOldNode,
    id: 'S3B_NPC_Recall_Negative',
    segmentId: '3B' as const,
    variantContent: {
      badWorld: {
        variants: oldNode.variantContent?.variants || [],
        placeholderPosition: oldNode.variantContent?.placeholderPosition || 0,
      },
    },
  };

  // Add the new nodes to the nodes object
  nodes['S3A_NPC_Recall_Positive'] = goodWorldNode;
  nodes['S3B_NPC_Recall_Negative'] = badWorldNode;

  // Remove old Segment 3 nodes
  const oldNodesToRemove = [
    'FinalSequence_Memory_Node',
    'GoodEnding1',
    'GoodEnding2',
    'BadEnding1',
    'BadEnding2',
    'GoodEpilogue',
    'BadEpilogue',
    'END_OF_NARRATIVE',
  ];

  oldNodesToRemove.forEach((nodeId) => {
    delete nodes[nodeId];
  });

  if (MIGRATION_DEBUG) console.log('Segment 3 migration completed');

  // Update currentNodeId if it points to an old node
  let currentNodeId = state.narrativeState.currentNodeId;
  if (oldNodesToRemove.includes(currentNodeId)) {
    // Default to the start of the good world
    currentNodeId = 'S3A_NPC_Recall_Positive';
  }

  // Remove the deprecated world_state_for_final_sequence variable
  const variables = { ...state.narrativeState.variables };
  delete variables.world_state_for_final_sequence;

  return {
    ...state,
    narrativeState: {
      ...state.narrativeState,
      nodes,
      currentNodeId,
      variables,
    },
  };
}

// Comprehensive node ID migration for Segments 1, 2, and remaining Segment 3 nodes
function migrateNodeIds(state: PersistedState): PersistedState {
  if (MIGRATION_DEBUG) {
    console.log('Starting comprehensive node ID migration...');
    console.log(`Migrating ${MIGRATION_STATS.totalMappings} nodes across all segments`);
  }
  if (!state.narrativeState || !state.narrativeState.nodes) {
    return state;
  }

  const { nodes, currentNodeId, nodeCompletion = {}, worldCompletion = {} } = state.narrativeState;
  const newNodes: typeof nodes = {};
  const newNodeCompletion: typeof nodeCompletion = {};
  const newWorldCompletion: typeof worldCompletion = {};

  // Step 1: Migrate all nodes with new IDs
  for (const [nodeId, node] of Object.entries(nodes) as [string, any][]) {
    const newId = getNewNodeId(nodeId);

    if (newId) {
      // This node needs to be migrated
      const migratedNode = {
        ...node,
        id: newId,
        // Update parent references
        parentIds: node.parentIds?.map((pid: string) => getNewNodeId(pid) || pid) || [],
        // Update child references
        childIds: node.childIds?.map((cid: string) => getNewNodeId(cid) || cid) || [],
        // Update choice destinations
        choices:
          node.choices?.map((choice: any) => ({
            ...choice,
            goto: getNewNodeId(choice.goto) || choice.goto,
          })) || [],
      };

      newNodes[newId] = migratedNode;
      if (MIGRATION_DEBUG) console.log(`Migrated node: ${nodeId} → ${newId}`);
    } else if (!isLegacyNode(nodeId)) {
      // Keep nodes that don't need migration but update their references
      newNodes[nodeId] = {
        ...node,
        // Update parent references
        parentIds: node.parentIds?.map((pid: string) => getNewNodeId(pid) || pid) || [],
        // Update child references
        childIds: node.childIds?.map((cid: string) => getNewNodeId(cid) || cid) || [],
        // Update choice destinations
        choices:
          node.choices?.map((choice: any) => ({
            ...choice,
            goto: getNewNodeId(choice.goto) || choice.goto,
          })) || [],
      };
    } else {
      if (MIGRATION_DEBUG) console.log(`Removing legacy node: ${nodeId}`);
    }
  }

  // Step 2: Migrate currentNodeId
  const newCurrentNodeId = getNewNodeId(currentNodeId) || currentNodeId;
  if (MIGRATION_DEBUG && newCurrentNodeId !== currentNodeId) {
    console.log(`Migrated currentNodeId: ${currentNodeId} → ${newCurrentNodeId}`);
  }

  // Step 3: Migrate node completion tracking
  for (const [nodeId, completion] of Object.entries(nodeCompletion) as [string, any][]) {
    const newId = getNewNodeId(nodeId);
    if (newId) {
      newNodeCompletion[newId] = completion;
    } else if (!isLegacyNode(nodeId)) {
      newNodeCompletion[nodeId] = completion;
    }
  }

  // Step 4: Migrate world-specific completion tracking
  for (const [world, worldNodes] of Object.entries(worldCompletion) as [
    string,
    Record<string, any>,
  ][]) {
    newWorldCompletion[world] = {};
    for (const [nodeId, completion] of Object.entries(worldNodes) as [string, any][]) {
      const newId = getNewNodeId(nodeId);
      if (newId) {
        newWorldCompletion[world][newId] = completion;
      } else if (!isLegacyNode(nodeId)) {
        newWorldCompletion[world][nodeId] = completion;
      }
    }
  }

  if (MIGRATION_DEBUG) {
    console.log('Node ID migration completed successfully');
    console.log(`Total nodes migrated: ${Object.keys(NODE_ID_MAPPINGS).length}`);
    console.log(`Legacy nodes removed: ${LEGACY_NODES_TO_REMOVE.length}`);
  }

  return {
    ...state,
    narrativeState: {
      ...state.narrativeState,
      nodes: newNodes,
      currentNodeId: newCurrentNodeId,
      nodeCompletion: newNodeCompletion,
      worldCompletion: newWorldCompletion,
    },
  };
}

export function migratePersistedState(state: any): PersistedState {
  const version = state.version || 0;

  if (version < 1) {
    if (
      state.planningState &&
      state.planningState.location &&
      !state.planningState.locationAtmosphere
    ) {
      state.planningState.locationAtmosphere = state.planningState.location;
    }
    delete state.planningState?.location;
    delete state.planningState?.underlyingReason;
    delete state.planningState?.behaviorsAndAims;
  }

  if (version < 2) {
    state = migrateSegment3Nodes(state);
  }

  if (version < 3) {
    state = migrateNodeIds(state);
  }

  return { ...state, version: STATE_VERSION } as PersistedState;
}
